/**
 * NotificationCenter
 *
 * Bell icon with badge showing count of pending/completed jobs.
 * Dropdown displays recent jobs with quick actions.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBackgroundReports } from '../contexts/BackgroundReportContext';
import { useChatSupport } from '../contexts/ChatSupportContext';
import { useQuickStartAssistant } from '../contexts/QuickStartAssistantContext';
import { useNavigate } from 'react-router-dom';
import WorkflowJobsPanel from './WorkflowJobsPanel';
import { jobsService } from '../services/jobsService';
import { fetchWithActivity } from '../utils/fetchWithActivity';
import { FaRobot } from 'react-icons/fa';
import { BETA_TOOL_LABELS } from '../constants/betaToolLabels';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface AssistantTaskNotification {
  id: string;
  taskId: string;
  conversationId: string;
  message: string;
  createdAt: string;
}

const formatWorkflowType = (workflowType: string): string => {
  const typeMap: Record<string, string> = {
    ...BETA_TOOL_LABELS,
    'loss_run_analysis': 'Loss Run Analysis',
    'loss_run_analyzer': 'Loss Run Analyzer',
    'loss_run_analyzer_v4': 'Loss Run Analyzer (legacy NEW)',
    'loss_run_analyzer_v4_3': 'Loss Run Analysis',
    'renewal_analysis': 'Renewal Analysis',
    'year_over_year': 'Year Over Year',
    'yoy_renewal': 'YOY Renewal',
    'personal_lines_yoy_renewal': 'Personal Lines YOY',
    'commercial_lines_exec_renewal': 'Commercial Lines Exec',
    'coverage_analysis': 'Coverage Analysis',
    'insurance_requirements': 'Insurance Requirements',
    'document_comparison': 'Document Comparison',
    'research': 'Research',
    'policy_comparison': 'Policy Comparison',
    'financial_analysis': 'Financial Analysis',
    'client_renewal_presentation': 'Client Renewal Presentation',
    'certificate_of_effort': 'Certificate of Effort',
    'universal_analysis': 'Universal Analysis',
    'rate_analysis': 'Rate Analysis',
    'portfolio_analyzer': 'Portfolio Analyzer',
    'homeowners_comparison': 'Homeowners Insurance Comparison',
    'personal_umbrella': 'Personal Umbrella Analysis',
  };

  return typeMap[workflowType] || workflowType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

interface AgentRunNotification {
  id: string;
  status: string;
  mode: string;
  subject: string | null;
  summary: string;
  finishedAt: string | null;
}

const AGENT_SEEN_KEY = 'agentSeenRuns';
function readSeenAgentRuns(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(AGENT_SEEN_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

const NotificationCenter: React.FC = () => {
  const { user, token } = useAuth();
  const { pendingReports, completedReports, totalCount, pendingCount, clearAllCompleted, markNotificationSent, removeReport, refreshReports } = useBackgroundReports();
  const { openChat } = useChatSupport();
  const { switchConversation } = useQuickStartAssistant();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isJobsPanelOpen, setIsJobsPanelOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [taskNotifications, setTaskNotifications] = useState<AssistantTaskNotification[]>([]);
  const [agentRuns, setAgentRuns] = useState<AgentRunNotification[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTaskNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/assistant/task-notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setTaskNotifications(await res.json());
    } catch {
      // Non-critical — the bell still shows jobs/reminders without these.
    }
  }, [token]);

  useEffect(() => {
    fetchTaskNotifications();
  }, [fetchTaskNotifications]);

  // Agent Mailbox runs (AGENT_MAILBOX_PLAN.md §8.2): finished runs and runs
  // waiting on approval, until the user opens them. "Seen" is per-browser.
  const fetchAgentRuns = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/agent/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      const seen = readSeenAgentRuns();
      setAgentRuns((data.runs || []).filter((r: AgentRunNotification) => r.status === 'awaiting_approval' || !seen.has(`${r.id}:${r.status}`)).slice(0, 6));
    } catch {
      // Non-critical.
    }
  }, [token]);

  useEffect(() => {
    fetchAgentRuns();
    const t = setInterval(fetchAgentRuns, 60000);
    return () => clearInterval(t);
  }, [fetchAgentRuns]);

  const handleAgentRunClick = (run: AgentRunNotification) => {
    setIsDropdownOpen(false);
    const seen = readSeenAgentRuns();
    seen.add(`${run.id}:${run.status}`);
    try {
      localStorage.setItem(AGENT_SEEN_KEY, JSON.stringify(Array.from(seen).slice(-200)));
    } catch {
      /* storage full / disabled */
    }
    setAgentRuns((prev) => prev.filter((r) => r.id !== run.id || r.status === 'awaiting_approval'));
    navigate(`/agent?run=${run.id}`);
  };

  const handleTaskNotificationClick = async (notification: AssistantTaskNotification) => {
    setIsDropdownOpen(false);
    setTaskNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    try {
      await fetchWithActivity(`${API_BASE_URL}/api/assistant/task-notifications/${notification.id}/read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // Best-effort — worst case it reappears on next fetch.
    }
    await switchConversation(notification.conversationId);
    openChat();
  };

  // Don't render if user is not logged in
  if (!user) return null;

  const handleViewReport = async (savedReportId: string, workflowId: string) => {
    // Mark notification as sent on server so it doesn't keep showing
    await markNotificationSent(workflowId);
    navigate(`/workflow-report?reportId=${savedReportId}`);
    setIsDropdownOpen(false);
  };

  const handleViewAllJobs = () => {
    setIsDropdownOpen(false);
    setIsJobsPanelOpen(true);
  };

  const handleCancel = async (workflowId: string) => {
    setCancellingId(workflowId);
    try {
      await jobsService.cancelJob(token || '', workflowId);
      removeReport(workflowId);
      await refreshReports();
    } catch (err) {
      console.error('Failed to cancel job:', err);
    } finally {
      setCancellingId(null);
    }
  };

  const handleClearAll = async () => {
    setIsClearing(true);
    try {
      await clearAllCompleted();
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    } finally {
      setIsClearing(false);
    }
  };

  // Combine all reports for display, showing most recent first
  const allReports = [...completedReports, ...pendingReports]
    .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
    .slice(0, 5);

  // Renewals + recommendations live in AttentionCenter's own bell now — this
  // dot only covers assistant task outcomes, which belong here (a task
  // firing is a completed/failed run, same category as a workflow job).
  const otherNotificationCount = taskNotifications.length + agentRuns.length;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="relative p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        aria-label="Notifications"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Job count badge — the number that matters ("how many are running/done") */}
        {totalCount > 0 && (
          <span className={`absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-primary rounded-full ${pendingCount > 0 ? 'animate-pulse' : ''}`}>
            {totalCount > 9 ? '9+' : totalCount}
          </span>
        )}

        {/* Processing dot when jobs are running but haven't produced a count yet */}
        {pendingCount > 0 && totalCount === 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-50"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
          </span>
        )}

        {/* Assistant task outcomes — a plain dot, not folded into the job count */}
        {otherNotificationCount > 0 && (
          <span
            className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-amber-500 ring-2 ring-white dark:ring-gray-900"
            title={`${otherNotificationCount} notification${otherNotificationCount !== 1 ? 's' : ''} from Arie`}
          />
        )}
      </button>

      {/* Dropdown */}
      {isDropdownOpen && (
        <div className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] max-h-[75vh] flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Notifications
              </h3>
              <div className="flex items-center gap-2">
                {pendingCount > 0 && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
                    {pendingCount} processing
                  </span>
                )}
                {completedReports.length > 0 && (
                  <button
                    onClick={handleClearAll}
                    disabled={isClearing}
                    className="px-2 py-0.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                    title="Clear all completed"
                  >
                    {isClearing ? 'Clearing...' : 'Clear All'}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          {/* Assistant tasks that fired (Phase 4) */}
          {taskNotifications.length > 0 && (
            <div className="border-b border-gray-200 dark:border-gray-700">
              <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Arie
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {taskNotifications.map((notification) => (
                  <button
                    key={notification.id}
                    onClick={() => handleTaskNotificationClick(notification)}
                    className="w-full p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-start gap-2 text-left"
                  >
                    <FaRobot className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-900 dark:text-white">{notification.message}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Agent Mailbox runs */}
          {agentRuns.length > 0 && (
            <div className="border-b border-gray-200 dark:border-gray-700">
              <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Agent</div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {agentRuns.map((run) => (
                  <button key={`${run.id}:${run.status}`} onClick={() => handleAgentRunClick(run)} className="w-full p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left">
                    <p className="text-sm text-gray-900 dark:text-white truncate">
                      {run.status === 'awaiting_approval' ? '✋ Needs approval: ' : run.status === 'failed' ? '⛔ Failed: ' : '📧 '}
                      {run.subject || 'Agent run'}
                    </p>
                    {run.summary && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{run.summary}</p>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Job List */}
          <div>
            {(taskNotifications.length > 0 || agentRuns.length > 0) && (
              <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Workflow jobs</div>
            )}
            {allReports.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                No recent workflow jobs
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {allReports.map((report) => (
                  <div
                    key={report.workflowId}
                    className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {formatWorkflowType(report.workflowType)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {report.customerName}
                        </p>
                      </div>
                      <div className="ml-3 flex-shrink-0">
                        {report.status === 'completed' && report.savedReportId ? (
                          <button
                            onClick={() => handleViewReport(report.savedReportId!, report.workflowId)}
                            className="text-xs text-primary hover:text-blue-700 dark:hover:text-blue-400 font-medium"
                          >
                            View
                          </button>
                        ) : report.status === 'processing' ? (
                          <div className="flex items-center gap-2">
                            <span className="flex items-center text-xs text-blue-600 dark:text-blue-400">
                              <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                  fill="none"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                              Processing
                            </span>
                            <button
                              onClick={() => handleCancel(report.workflowId)}
                              disabled={cancellingId === report.workflowId}
                              className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 underline disabled:opacity-50"
                              title="Cancel workflow"
                            >
                              {cancellingId === report.workflowId ? '...' : 'Cancel'}
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {report.status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <button
              onClick={handleViewAllJobs}
              className="w-full px-4 py-2 text-sm text-center text-primary hover:text-blue-700 dark:hover:text-blue-400 font-medium"
            >
              View All Jobs
            </button>
          </div>
        </div>
      )}

      {/* Jobs Panel Modal */}
      <WorkflowJobsPanel isOpen={isJobsPanelOpen} onClose={() => setIsJobsPanelOpen(false)} />
    </div>
  );
};

export default NotificationCenter;
